// saas-monetization-plans v2 — WU-M: SubscriptionPage.
//
// Org Admin view of the org's OWN SaaS subscription to JQRACE4 (plan,
// status, usage vs quota, invoices, overage charges) plus the legal-friction
// cancel flow (design §6/§8, spec "Org Admin Visibility" /
// "Subscription Lifecycle — Legal cancellation"). This is DELIBERATELY a
// separate, clearly-labeled area from the existing "Billing" tab, which
// manages the org's OWN Stripe Connect account for receiving VISITOR
// payments — a different thing entirely from this platform subscription.

import { listSubscriptionInvoices, listSubscriptionOverageCharges } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import { DEFAULT_CURRENCY, type SubscriptionResponse } from '@jq4race/shared';
import {
  Badge,
  type BadgeProps,
  Card,
  CardContent,
  formatCents,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from '@jq4race/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, TriangleAlert, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { enumLabel } from '../../../../shared/i18n/enum-label';
import { CancelSubscriptionDialog } from '../components/CancelSubscriptionDialog';
import { SubscriptionStatusNotice } from '../components/SubscriptionStatusNotice';
import {
  OWN_SUBSCRIPTION_QUERY_KEY,
  SUBSCRIPTION_QUERY_PREFIX,
  useOwnSubscription,
} from '../hooks/useOwnSubscription';

// ── Labels ───────────────────────────────────────────────────────────────

const STATUS_TONE: Record<SubscriptionResponse['status'], BadgeProps['variant']> = {
  incomplete: 'secondary',
  active: 'success',
  past_due: 'warning',
  suspended: 'danger',
  canceled: 'secondary',
};

// ── Component ────────────────────────────────────────────────────────────

export function SubscriptionPage() {
  const { t, i18n } = useTranslation('subscription');

  const STATUS_LABEL: Record<SubscriptionResponse['status'], string> = {
    incomplete: t('status.incomplete'),
    active: t('status.active'),
    past_due: t('status.pastDue'),
    suspended: t('status.suspended'),
    canceled: t('status.canceled'),
  };

  const FEATURE_LABEL: Record<keyof Omit<SubscriptionResponse['features'], 'supportTier'>, string> =
    {
      multiLocation: t('features.multiLocation'),
      sms: t('features.sms'),
      damageReports: t('features.damageReports'),
      advancedAnalytics: t('features.advancedAnalytics'),
      customBranding: t('features.customBranding'),
      whiteLabel: t('features.whiteLabel'),
      api: t('features.api'),
    };

  const SUPPORT_TIER_LABEL: Record<SubscriptionResponse['features']['supportTier'], string> = {
    email: t('supportTier.email'),
    priority: t('supportTier.priority'),
    dedicated: t('supportTier.dedicated'),
  };

  const queryClient = useQueryClient();
  // The subscription query is shared with the app-shell banner and the
  // dashboard card (one key, one request); invoices/overage are page-only.
  const subscriptionQuery = useOwnSubscription();
  const invoicesQuery = useQuery({
    queryKey: [...SUBSCRIPTION_QUERY_PREFIX, 'invoices'],
    queryFn: () => listSubscriptionInvoices(),
  });
  const overageQuery = useQuery({
    queryKey: [...SUBSCRIPTION_QUERY_PREFIX, 'overage-charges'],
    queryFn: () => listSubscriptionOverageCharges(),
  });

  const subscription = subscriptionQuery.data ?? null;
  const invoices = invoicesQuery.data?.invoices ?? [];
  const overageCharges = overageQuery.data?.overageCharges ?? [];
  const loading = subscriptionQuery.isLoading;

  const loadFailure = subscriptionQuery.error ?? invoicesQuery.error ?? overageQuery.error;
  const [errorDismissed, setErrorDismissed] = useState(false);
  const error =
    loadFailure && !errorDismissed
      ? translateApiError(loadFailure, t, { fallback: t('loadError') })
      : null;

  const [success, setSuccess] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  useEffect(() => {
    if (!success) return;
    const id = window.setTimeout(() => setSuccess(null), 5000);
    return () => window.clearTimeout(id);
  }, [success]);

  if (loading && !subscription) {
    return <p className="text-[13px] text-jq-graphite">{t('common:states.loading')}</p>;
  }

  if (error && !subscription) {
    return (
      <FeedbackBanner tone="error" message={error} onDismiss={() => setErrorDismissed(true)} />
    );
  }

  if (!subscription) {
    return null;
  }

  const featureKeys = Object.keys(FEATURE_LABEL) as Array<keyof typeof FEATURE_LABEL>;
  const enabledFeatures = featureKeys.filter((key) => subscription.features[key]);

  return (
    <div className="space-y-6">
      <p className="max-w-xl text-[14px] leading-relaxed text-jq-graphite">{t('intro')}</p>

      {error && (
        <FeedbackBanner tone="error" message={error} onDismiss={() => setErrorDismissed(true)} />
      )}
      {success && (
        <FeedbackBanner tone="success" message={success} onDismiss={() => setSuccess(null)} />
      )}

      {/* Same component (and same manual-vs-card copy) as the app-shell banner. */}
      {(subscription.status === 'suspended' || subscription.status === 'past_due') && (
        <SubscriptionStatusNotice
          status={subscription.status}
          graceUntil={subscription.graceUntil}
          collectionMethod={subscription.collectionMethod}
        />
      )}

      {/* ── Plan & status ────────────────────────────────────────────── */}
      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="space-y-4 p-6 sm:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[12px] uppercase tracking-widest text-jq-graphite">
                {t('plan.label')}
              </p>
              <p className="mt-1 text-[18px] font-medium text-jq-charcoal">
                {subscription.planName}
              </p>
            </div>
            <Badge variant={STATUS_TONE[subscription.status]}>
              {STATUS_LABEL[subscription.status]}
            </Badge>
          </div>

          {subscription.currentPeriodStart && subscription.currentPeriodEnd && (
            <p className="text-[13px] text-jq-graphite">
              {t('plan.currentPeriod', {
                start: formatDate(subscription.currentPeriodStart, i18n.language),
                end: formatDate(subscription.currentPeriodEnd, i18n.language),
              })}
            </p>
          )}

          {subscription.cancelAtPeriodEnd && subscription.currentPeriodEnd && (
            <p className="rounded-sm border border-jq-warning/40 bg-jq-warning/10 px-3 py-2 text-[13px] text-jq-charcoal">
              {t('plan.cancellationScheduled', {
                date: formatDate(subscription.currentPeriodEnd, i18n.language),
              })}
            </p>
          )}

          <div>
            <p className="text-[12px] uppercase tracking-widest text-jq-graphite">
              {t('plan.featuresLabel')}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {enabledFeatures.length === 0 ? (
                <span className="text-[13px] text-jq-graphite/60">{t('plan.noExtraFeatures')}</span>
              ) : (
                enabledFeatures.map((key) => (
                  <Badge key={key} variant="accent">
                    {FEATURE_LABEL[key]}
                  </Badge>
                ))
              )}
              <Badge variant="outline">
                {SUPPORT_TIER_LABEL[subscription.features.supportTier]}
              </Badge>
            </div>
          </div>

          <div>
            <p className="text-[12px] uppercase tracking-widest text-jq-graphite">
              {t('plan.commissionLabel')}
            </p>
            <p className="mt-1 text-[13px] text-jq-graphite">
              {subscription.commissionEnabled
                ? t('plan.commissionRate', {
                    rate: formatBps(subscription.commissionBasisPoints ?? 0),
                  })
                : t('plan.commissionNotApplicable')}
            </p>
          </div>

          {/* A suspended subscription is regularized by paying, not by cancelling it. */}
          {subscription.status !== 'canceled' && subscription.status !== 'suspended' && (
            <div className="border-t border-jq-sand/70 pt-4">
              <button
                type="button"
                onClick={() => setCancelDialogOpen(true)}
                className="text-[13px] font-medium text-jq-danger underline-offset-2 hover:underline"
              >
                {t('plan.cancelSubscription')}
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Usage vs quota ───────────────────────────────────────────── */}
      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="space-y-3 p-6 sm:p-8">
          <p className="text-[14px] font-medium text-jq-charcoal">{t('usage.title')}</p>
          {subscription.usage ? (
            <UsageBar usage={subscription.usage} />
          ) : (
            <p className="text-[13px] text-jq-graphite">{t('usage.pending')}</p>
          )}
        </CardContent>
      </Card>

      {/* ── Invoices ─────────────────────────────────────────────────── */}
      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          <p className="mb-4 text-[14px] font-medium text-jq-charcoal">{t('invoices.title')}</p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('invoices.period')}</TableHead>
                <TableHead>{t('invoices.breakdown')}</TableHead>
                <TableHead>{t('invoices.amount')}</TableHead>
                <TableHead>{t('invoices.status')}</TableHead>
                <TableHead className="text-right">{t('invoices.paid')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.length === 0 ? (
                <TableEmpty colSpan={5} message={t('invoices.empty')} />
              ) : (
                invoices.map((inv, idx) => (
                  // manual invoices carry providerInvoiceRef=null (multiple
                  // coexist — Postgres NULL-distinct uniqueness, design
                  // §1.2) so it alone is not a safe React key; prefer id/
                  // invoiceNumber, falling back to index.
                  <TableRow key={inv.id ?? inv.invoiceNumber ?? inv.providerInvoiceRef ?? idx}>
                    <TableCell className="text-jq-graphite">
                      {inv.periodStart && inv.periodEnd
                        ? t('invoices.periodRange', {
                            start: formatDate(inv.periodStart, i18n.language),
                            end: formatDate(inv.periodEnd, i18n.language),
                          })
                        : '—'}
                    </TableCell>
                    <TableCell className="text-jq-graphite">
                      {inv.lines.length === 0 ? (
                        '—'
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {/* `kind` is a stable key here: an invoice has at
                              most one base and one overage line (design §2.1). */}
                          {inv.lines.map((line) => (
                            <Badge
                              key={line.kind}
                              variant={line.kind === 'base' ? 'outline' : 'accent'}
                              title={line.description}
                            >
                              {enumLabel(t, 'invoiceLineKind', line.kind)}:{' '}
                              {formatCents(line.amountCents, inv.currency)}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-jq-graphite">
                      {formatCents(inv.amountCents, inv.currency)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={INVOICE_STATUS_TONE[inv.status]} className="capitalize">
                        {enumLabel(t, 'invoiceStatus', inv.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-jq-graphite">
                      {inv.paidAt ? formatDate(inv.paidAt, i18n.language) : '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ── Overage charges ──────────────────────────────────────────── */}
      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          <p className="mb-4 text-[14px] font-medium text-jq-charcoal">
            {t('overageCharges.title')}
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('overageCharges.period')}</TableHead>
                <TableHead>{t('overageCharges.vehicles')}</TableHead>
                <TableHead>{t('overageCharges.overageUnits')}</TableHead>
                <TableHead className="text-right">{t('overageCharges.amount')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {overageCharges.length === 0 ? (
                <TableEmpty colSpan={4} message={t('overageCharges.empty')} />
              ) : (
                overageCharges.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="text-jq-graphite">
                      {t('overageCharges.periodRange', {
                        start: formatDate(c.periodStart, i18n.language),
                        end: formatDate(c.periodEnd, i18n.language),
                      })}
                    </TableCell>
                    <TableCell className="text-jq-graphite tabular-nums">
                      {c.vehicleCount} / {c.includedVehicles}
                    </TableCell>
                    <TableCell className="text-jq-graphite tabular-nums">
                      {c.overageUnits}
                    </TableCell>
                    <TableCell className="text-right text-jq-graphite">
                      {formatCents(c.amountCents, c.currency)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <CancelSubscriptionDialog
        open={cancelDialogOpen}
        currentPeriodEnd={subscription.currentPeriodEnd}
        onOpenChange={setCancelDialogOpen}
        onCanceled={(result) => {
          // Reflect the result immediately, then refetch every subscription
          // query (this page, the shell banner, the dashboard card).
          queryClient.setQueryData<SubscriptionResponse>(OWN_SUBSCRIPTION_QUERY_KEY, (prev) =>
            prev
              ? {
                  ...prev,
                  status: result.status,
                  cancelAtPeriodEnd: result.cancelAtPeriodEnd,
                  cancellationReason: result.cancellationReason,
                }
              : prev,
          );
          void queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_PREFIX });
          setSuccess(t('cancellationConfirmed'));
        }}
      />
    </div>
  );
}

// ── Usage bar ────────────────────────────────────────────────────────────

function UsageBar({ usage }: { usage: NonNullable<SubscriptionResponse['usage']> }) {
  const { t, i18n } = useTranslation('subscription');
  const pct =
    usage.includedVehicles > 0
      ? Math.min(100, Math.round((usage.vehicleCount / usage.includedVehicles) * 100))
      : usage.vehicleCount > 0
        ? 100
        : 0;
  const overLimit = usage.overageUnits > 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-[13px] text-jq-graphite">
        <span>
          {t('usage.vehicleCount', { count: usage.vehicleCount, total: usage.includedVehicles })}
        </span>
        <span>
          {t('usage.period', {
            start: formatDate(usage.periodStart, i18n.language),
            end: formatDate(usage.periodEnd, i18n.language),
          })}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-jq-surface-2">
        <div
          className={`h-full rounded-full ${overLimit ? 'bg-jq-danger' : 'bg-jq-success'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {overLimit && (
        <p className="text-[13px] text-jq-danger">
          {t('usage.overQuota', {
            count: usage.overageUnits,
            amount: formatCents(usage.overageAmountCents, DEFAULT_CURRENCY),
          })}
        </p>
      )}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────

const INVOICE_STATUS_TONE: Record<string, BadgeProps['variant']> = {
  open: 'warning',
  paid: 'success',
  uncollectible: 'danger',
  void: 'secondary',
};

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

/** Format bps as a human-readable percentage string, e.g. 500 → "5.00%" */
function formatBps(bps: number): string {
  return `${(bps / 100).toFixed(2)}%`;
}

// ── Feedback banner ────────────────────────────────────────────────────────

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('subscription');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('dismissMessage')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
